import type { RumEvent, RumEventDomainContext } from "@datadog/browser-rum";

import { ROUTES_URL } from "@/constant";

const redactedValue = "[REDACTED]";
const routeTemplates = [...new Set(Object.values(ROUTES_URL))]
  .filter((template) => template.startsWith("/") && !/[?#*]/.test(template))
  .map((template) => ({ template, matcher: routeTemplateMatcher(template) }));

const sensitiveFieldPattern =
  /^(?:access.?token|refresh.?token|reset.?token|token|secret|password|passwd|authorization|xapikey|xauthtoken|xamzsecuritytoken|cookie|otp|one.?time.?password|pin|signature|fingerprint(?:image|template|token)?|mykad(?:photo|image)?|idtype|idnumber|identitytype|identitynumber|nationality|ethnicity|race|dateofbirth|birthdate|dob|sex|gender|occupation|tin|email(?:address)?|mobile(?:number)?|phone(?:number)?|dialcode|postalcode|zipcode|address(?:line[123]?|[123])?|bankaccount(?:number)?|accountnumber|cardnumber|username|userid|(?:customer|person|client|borrower)(?:name|id|identifier)|(?:first|middle|last|full|owner)name|loginid|hashkey)$/;
const emailPattern = /\b[A-Z0-9._%+-]+(?:@|%40)[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const approvedRumUserContextFields = new Set(["id", "name", "email"]);
// SDK-set build metadata. The release tag ends in a build timestamp that
// would otherwise match the phone/card number patterns.
const unscrubbedEventKeys = new Set(["version", "ddtags"]);
const labeledSensitiveValuePattern =
  /\b((?:(?:owner|pawner|residential|home|mailing|customer)[ _-]?)?(?:contact[ _-]?(?:number|no)|ic[ _-]?(?:number|no)|passport(?:[ _-]?(?:number|no))?|(?:(?:(?:customer|person|client|borrower)[ _-]?(?:full[ _-]?name|first[ _-]?name|last[ _-]?name|name|id(?:entifier|[ _-]?(?:type|number))?))|(?:full|first|middle|last|owner)[ _-]?name|id[ _-]?(?:type|number)|identity[ _-]?(?:type|number)|nationality|ethnicity|race|date[ _-]?of[ _-]?birth|birthdate|dob|sex|gender|occupation(?:[ _-]?details)?|tin|mobile(?:[ _-]?number)?|phone(?:[ _-]?number)?|dial[ _-]?code|email(?:[ _-]?address)?|postal[ _-]?code|zip[ _-]?code|address(?:[ _-]?(?:line[ _-]?[123]?|[123]))?|bank[ _-]?account(?:[ _-]?number)?|account[ _-]?number|card[ _-]?number|user[ _-]?name|login[ _-]?id|signature|fingerprint|mykad(?:photo|image|id|number)?|nric|icno))\s*[:=]\s*)(?:"[^"]*"|'[^']*'|[^,;\n|}]+)/gi;

function routeTemplateMatcher(template: string): RegExp {
  const pattern = template
    .split("/")
    .map((segment) =>
      /^:[A-Za-z0-9_-]+$/.test(segment) ? "[^/]+" : segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    )
    .join("/");

  return new RegExp(`^${pattern}/?$`);
}

export function normalizeViewName(path: string): string | undefined {
  try {
    let pathname = new URL(path, "https://rum.invalid").pathname;

    for (let attempt = 0; attempt < 4; attempt += 1) {
      const decodedPathname = decodeURIComponent(pathname);
      if (decodedPathname === pathname) {
        break;
      }
      pathname = decodedPathname;
    }

    if (/%[\da-f]{2}/i.test(pathname)) {
      return undefined;
    }

    return routeTemplates.find(({ matcher }) => matcher.test(pathname))?.template;
  } catch {
    return undefined;
  }
}

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[_\-\s]/g, "");
}

function isUrlField(key: string): boolean {
  return /(?:url|uri|href|referrer|sourceurl|resourceurl|requesturl)$/i.test(key);
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value.replace(/\+/g, " "));
  } catch {
    return value;
  }
}

function redactCardNumbers(value: string): string {
  return value.replace(/(^|[^\d])((?:\d[ -]?){13,19})(?=$|[^\d])/g, (match, prefix, candidate) => {
    const digits = candidate.replace(/\D/g, "");
    let checksum = 0;
    let doubleDigit = false;

    for (let index = digits.length - 1; index >= 0; index -= 1) {
      let digit = Number(digits[index]);
      if (doubleDigit) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      checksum += digit;
      doubleDigit = !doubleDigit;
    }

    return digits.length >= 13 && digits.length <= 19 && checksum % 10 === 0
      ? `${prefix}${redactedValue}`
      : match;
  });
}

function sanitizeText(value: string): string {
  const sanitized = value
    .replace(labeledSensitiveValuePattern, `$1${redactedValue}`)
    .replace(/\bBearer\s+[A-Za-z0-9._~+/-]+=*/gi, "Bearer [REDACTED]")
    .replace(/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g, redactedValue)
    .replace(emailPattern, redactedValue)
    .replace(/\b[STFGM]\d{7}[A-Z]\b/gi, redactedValue)
    .replace(
      /\b\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[ -]?\d{2}[ -]?\d{4}\b/g,
      redactedValue,
    )
    .replace(/\+?65[ -]?[89]\d{3}[ -]?\d{4}\b/g, redactedValue)
    .replace(/\+?60[ -]?1\d[ -]?\d{3,4}[ -]?\d{4}\b/g, redactedValue)
    .replace(
      /((?:synthetic[-_])?(?:access[-_]?token|auth[-_]?token|client[-_]?token|id[-_]?token|session[-_]?token|refresh[-_]?(?:token|code)|reset[-_]?(?:token|code)|authorization[-_]?code|token|api[-_]?key|client[-_]?secret|secret|password|user[-_ ]?name|login[-_ ]?id|otp|pin|signature)\s*[:=]\s*)["']?[^,\s;&"'<>]+/gi,
      `$1${redactedValue}`,
    )
    .replace(
      /\bsynthetic-(?:(?:access|auth|client|id|session|refresh|reset|bearer|api)-(?:token|key|secret)|token|secret|password|otp|pin|signature)(?:[-_=:][A-Za-z0-9._~+/-]+)?\b/gi,
      redactedValue,
    )
    .replace(/https?:\/\/[^\s"'<>]+/gi, (url) => sanitizeUrl(url));

  return redactCardNumbers(sanitized);
}

function sanitizePath(path: string): string {
  const segments = path.split("/");
  return segments
    .map((segment) => {
      const decoded = safeDecode(segment);
      return sanitizeText(decoded) === decoded ? segment : redactedValue;
    })
    .join("/");
}

function sanitizeParameters(value: string, prefix: "?" | "#"): string {
  const parameters = value.slice(1).split("&");
  const sanitized = parameters.map((parameter) => {
    const equalsIndex = parameter.indexOf("=");
    if (equalsIndex < 0) {
      return parameter;
    }

    const rawKey = parameter.slice(0, equalsIndex);
    const rawValue = parameter.slice(equalsIndex + 1);
    const key = normalizeKey(safeDecode(rawKey));
    const decodedValue = safeDecode(rawValue);
    const valueChanged = sanitizeText(decodedValue) !== decodedValue;
    if (isSensitiveField(key, key) || valueChanged) {
      return `${rawKey}=${redactedValue}`;
    }
    return parameter;
  });

  return `${prefix}${sanitized.join("&")}`;
}

function sanitizeUrl(value: string): string {
  const match = value.match(/^((?:[A-Za-z][A-Za-z\d+.-]*:\/\/[^/?#]*)?)([^?#]*)(\?[^#]*)?(#.*)?$/);
  if (!match) {
    return sanitizeText(value);
  }

  let [, authority, path, query, fragment] = match;
  const protocolEnd = authority.indexOf("://") + 3;
  const credentialsAt = authority.lastIndexOf("@");
  if (protocolEnd > 2 && credentialsAt > protocolEnd) {
    authority = `${authority.slice(0, protocolEnd)}${redactedValue}@${authority.slice(credentialsAt + 1)}`;
  }

  path = sanitizePath(path);
  if (query) {
    query = sanitizeParameters(query, "?");
  }
  if (fragment) {
    fragment = fragment.includes("=") ? sanitizeParameters(fragment, "#") : sanitizeText(fragment);
  }

  return `${authority}${path}${query ?? ""}${fragment ?? ""}`;
}

function isSensitiveField(key: string, fieldPath: string): boolean {
  const normalizedKey = normalizeKey(key);
  return (
    sensitiveFieldPattern.test(normalizedKey) ||
    /^(?:(?:access|auth|client|id|session|bearer|refresh|reset)?token|(?:api|client|secret|private)key|jwt|refreshcode|resetcode|authcode|authorizationcode|otpcode)$/.test(
      normalizedKey,
    ) ||
    /(?:signature(?:mediaurl|image|data|payload|url)?|fingerprint(?:image|template|path|data|payload)?|fpimagepath|mykad(?:photo|image|id|no|number)?|nric|icno)$/.test(
      normalizedKey,
    ) ||
    /^(?:customer|person|client|borrower|user|pawner)(?:name|id|identifier|idtype|idnumber|identitytype|identitynumber|email(?:address)?|mobile(?:number)?|phone(?:number)?|address(?:line[123]?)?|postalcode|zipcode|dateofbirth|birthdate|dob|sex|gender|nationality|ethnicity|race|occupation|tin)$/.test(
      normalizedKey,
    ) ||
    // Prefixed names such as residential_address, contact_number, owner_dob or ownerIdNumber.
    /(?:email(?:address)?|mobile(?:number|no)?|phone(?:number|no)?|contact(?:number|no)|address(?:line[123]?|[123])?|postalcode|zipcode|dateofbirth|birthdate|dob|idnumber|idno|identitynumber|icnumber|icno|nric|passport(?:number|no)?|(?:first|middle|last|full)name|unitno)$/.test(
      normalizedKey,
    ) ||
    /^(?:ic|passport)$/.test(normalizedKey) ||
    (normalizedKey === "name" &&
      /(?:^|\.)(?:customer|customers|person|people|borrower|owner)(?:\.|$)/i.test(fieldPath))
  );
}

function sanitizeValue(
  value: unknown,
  key: string,
  seen: WeakMap<object, number>,
  forceRedact = false,
  fieldPath = key,
): unknown {
  if (forceRedact && (value === null || value === undefined)) {
    return value;
  }
  if (typeof value === "string") {
    if (isUrlField(key)) {
      return sanitizeUrl(value);
    }
    if (forceRedact || isSensitiveField(key, fieldPath)) {
      return redactedValue;
    }
    return sanitizeText(value);
  }
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value !== "object") {
    return forceRedact || isSensitiveField(key, fieldPath) ? redactedValue : value;
  }

  sanitizeObject(value, seen, forceRedact || isSensitiveField(key, fieldPath), fieldPath);
  return value;
}

function sanitizeObject(
  value: object,
  seen: WeakMap<object, number>,
  forceRedact: boolean,
  path = "",
): void {
  const mode = forceRedact ? 2 : 1;
  if ((seen.get(value) ?? 0) >= mode) {
    return;
  }
  seen.set(value, mode);

  let keys: string[];
  try {
    keys = Object.keys(value);
  } catch {
    return;
  }

  for (const key of keys) {
    try {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!descriptor) {
        continue;
      }
      if (!("value" in descriptor)) {
        if (descriptor.configurable) {
          Object.defineProperty(value, key, {
            configurable: descriptor.configurable,
            enumerable: descriptor.enumerable,
            writable: true,
            value: redactedValue,
          });
        }
        continue;
      }

      if (!path && unscrubbedEventKeys.has(key)) {
        continue;
      }

      const fieldPath = path ? `${path}.${key}` : key;
      const redactRumUserIdentity =
        !forceRedact && path === "usr" && approvedRumUserContextFields.has(key);
      const sanitized = sanitizeValue(
        descriptor.value,
        key,
        seen,
        forceRedact || redactRumUserIdentity,
        fieldPath,
      );
      if (sanitized !== descriptor.value) {
        Object.defineProperty(value, key, { ...descriptor, value: sanitized });
      }
    } catch {
      // A malformed/custom property must not cause an otherwise valid event to be dropped.
    }
  }
}

export function scrubRumEvent(
  event: RumEvent,
  _context?: RumEventDomainContext,
  _viewPrefix?: string,
): boolean {
  try {
    if (event && typeof event === "object") {
      sanitizeObject(event as unknown as object, new WeakMap(), false);
    }
  } catch {
    // Keep the event. The SDK event model is plain data; this is a defensive custom-shape fallback.
  }
  return true;
}
